import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, CheckCircle, Sparkles } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

const Home = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const testimonials = [
    {
      name: 'Alexandre D.',
      rating: 5,
      text: 'Service exceptionnel ! Ma Mercedes n’a jamais été aussi brillante. Le polissage est impeccable, aucune trace, aucun hologramme. Je recommande vivement !',
    },
    {
      name: 'Sophie M.',
      rating: 5,
      text: 'Un travail d’une précision remarquable. L’équipe est professionnelle et passionnée. La protection céramique a transformé ma voiture.',
    },
    {
      name: 'Thomas R.',
      rating: 5,
      text: 'Je suis venu pour une correction de peinture et le résultat dépasse mes attentes. Un vrai savoir-faire ! SUKI est une référence à Montmagny.',
    },
    {
      name: 'Marie L.',
      rating: 5,
      text: 'Attention aux détails incroyable. Ils ont pris le temps de tout expliquer et le résultat est juste parfait. Mon Audi brille comme au premier jour.',
    },
  ];

  const services = [
    {
      title: 'Polissage Professionnel',
      description: 'Correction de peinture sans hologrammes pour une finition parfaite',
      image: 'https://images.unsplash.com/photo-1708805282695-ef186db20192?crop=entropy&cs=srgb&fm=jpg&q=85',
    },
    {
      title: 'Protection Céramique',
      description: 'Protection longue durée pour une brillance exceptionnelle',
      image: 'https://customer-assets.emergentagent.com/job_dc9381ad-1d56-41f1-8e2a-40efea919a71/artifacts/9hww8bwe_image.png',
    },
    {
      title: 'Lavage Détaillé',
      description: 'Nettoyage méticuleux extérieur avec décontamination complète',
      image: 'https://customer-assets.emergentagent.com/job_dc9381ad-1d56-41f1-8e2a-40efea919a71/artifacts/d7cw6mvw_image.png',
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image - Desktop */}
        <div
          className="hidden lg:block absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_7b719bdf-e077-4e3c-9fc3-d2b972d92c26/artifacts/jg22img9_image.png)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        </div>
        
        {/* Background Image - Mobile - Ferrari Rouge */}
        <div
          className="block lg:hidden absolute inset-0 bg-cover"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_dc9381ad-1d56-41f1-8e2a-40efea919a71/artifacts/lupjuxb0_image.png)',
            backgroundPosition: 'center 40%',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <div className="max-w-3xl animate-fadeIn">
            <div className="flex items-center space-x-2 mb-6">
              <Sparkles className="text-[#c9a961]" size={24} />
              <span className="text-[#c9a961] font-medium tracking-wider uppercase text-sm">Excellence Automobile</span>
            </div>
            <h1 className="hero-title text-6xl md:text-7xl lg:text-8xl mb-6 leading-tight">
              Detailing Haut de Gamme à Paris
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-200 leading-relaxed">
              Polissage professionnel, correction de peinture et protection céramique pour révéler la beauté de votre véhicule
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/reservation"
                className="inline-flex items-center justify-center px-8 py-4 bg-[#c9a961] text-white rounded-full text-lg font-semibold hover:bg-[#b8984e] transition-all duration-300 luxury-button shadow-lg"
                data-testid="hero-book-button"
              >
                Réserver maintenant
                <ArrowRight className="ml-2" size={20} />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-md text-white rounded-full text-lg font-semibold hover:bg-white/20 transition-all duration-300 border border-white/30"
                data-testid="hero-services-button"
              >
                Découvrir nos services
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/70 rounded-full" />
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-[#f8f7f5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 animate-slideInLeft">
              <h2 className="section-title text-5xl md:text-6xl text-[#1a1a1a] gold-line pb-4">
                Bienvenue chez SUKI
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                Chez nous, votre voiture reçoit l'attention et le traitement qu'elle mérite. Nous sommes spécialisés dans le lavage détaillé de l'extérieur, la décontamination, le polissage professionnel et les protections haut de gamme.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Chaque traitement est réalisé avec une précision maximale et une attention particulière aux détails, pour que votre voiture ait une brillance luxueuse et une finition impeccable et durable.
              </p>
              <div className="grid grid-cols-2 gap-6 pt-6">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-[#c9a961] mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Expertise</h3>
                    <p className="text-gray-600 text-sm">Professionnels formés</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-[#c9a961] mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Précision</h3>
                    <p className="text-gray-600 text-sm">Sans hologrammes</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-[#c9a961] mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Qualité</h3>
                    <p className="text-gray-600 text-sm">Produits premium</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-[#c9a961] mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Passion</h3>
                    <p className="text-gray-600 text-sm">Dévouement total</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative h-[500px] lg:h-[600px] rounded-2xl overflow-hidden shadow-2xl animate-slideInRight">
              {/* Image Desktop et Mobile - Nouvelle photo avec plaque SUKI visible */}
              <img
                src="https://customer-assets.emergentagent.com/job_dc9381ad-1d56-41f1-8e2a-40efea919a71/artifacts/009e2hyz_image.png"
                alt="Mercedes AMG GT - SUKI detailing work"
                className="w-full h-full object-cover"
                style={{ objectPosition: 'center 65%' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title text-5xl md:text-6xl text-[#1a1a1a] mb-4 gold-line inline-block pb-4">
              Nos Services
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto mt-6">
              Des prestations sur-mesure pour sublimer votre véhicule
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card
                key={index}
                className="group overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer"
                data-testid={`service-card-${index}`}
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                </div>
                <CardContent className="p-6 -mt-16 relative z-10">
                  <h3 className="text-2xl font-semibold text-white mb-2 section-title">{service.title}</h3>
                  <p className="text-gray-300 mb-4">{service.description}</p>
                  <Link
                    to="/services"
                    className="inline-flex items-center text-[#c9a961] font-medium hover:gap-2 transition-all duration-300"
                  >
                    En savoir plus
                    <ArrowRight size={18} className="ml-1" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center px-8 py-4 bg-[#1a1a1a] text-white rounded-full text-lg font-semibold hover:bg-[#2a2a2a] transition-all duration-300 luxury-button"
              data-testid="view-all-services-button"
            >
              Voir tous nos services
              <ArrowRight className="ml-2" size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-[#1a1a1a] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1605437241278-c1806d14a4d9?q=85)' }} />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="section-title text-5xl md:text-6xl mb-4 gold-line inline-block pb-4">Avis Clients</h2>
            <p className="text-gray-400 text-lg mt-6">Ce que nos clients disent de nous</p>
          </div>

          <div className="relative" data-testid="testimonials-carousel">
            <Card className="bg-white/5 backdrop-blur-md border-white/10">
              <CardContent className="p-8 md:p-12">
                <div className="flex justify-center mb-6">
                  {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                    <Star key={i} className="text-[#c9a961] fill-current" size={24} />
                  ))}
                </div>
                <p className="text-xl md:text-2xl text-center mb-8 leading-relaxed text-gray-400">
                  "{testimonials[currentTestimonial].text}"
                </p>
                <p className="text-center text-[#c9a961] font-semibold text-lg">
                  {testimonials[currentTestimonial].name}
                </p>
              </CardContent>
            </Card>

            {/* Dots */}
            <div className="flex justify-center mt-8 space-x-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentTestimonial ? 'bg-[#c9a961] w-8' : 'bg-white/30'
                  }`}
                  aria-label={`Testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>

          <div className="text-center mt-12">
            <Link
              to="/reviews"
              className="inline-flex items-center text-[#c9a961] font-medium hover:gap-2 transition-all duration-300 text-lg"
            >
              Voir tous les avis
              <ArrowRight size={20} className="ml-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="relative py-32 bg-cover bg-center bg-fixed"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_d9fd72fd-f35d-49e6-8f9e-e0391db52cc1/artifacts/jca1ulxy_image.png)',
        }}
      >
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="hero-title text-5xl md:text-6xl mb-6">Prêt à transformer votre véhicule ?</h2>
          <p className="text-xl mb-8 text-gray-200">
            Réservez votre rendez-vous dès maintenant et découvrez l'excellence du detailing automobile
          </p>
          <Link
            to="/reservation"
            className="inline-flex items-center px-10 py-5 bg-[#c9a961] text-white rounded-full text-xl font-semibold hover:bg-[#b8984e] transition-all duration-300 luxury-button shadow-2xl"
            data-testid="cta-book-button"
          >
            Réserver un rendez-vous
            <ArrowRight className="ml-2" size={24} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
